import { get, uniq } from "lodash-es";
import { type ComponentType, type FC, type PropsWithChildren, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type InjectedFormProps, reduxForm } from "redux-form";
import type { Value } from "ts-toolbelt/out/Misc/JSON/Value";
import { ActionButtonIntent, Dialog, VStack } from "widgets";
import { useControlledDialog } from "widgets/hooks";
import { getFilenameInfo } from "widgets/utils";

import { pluralizeWord } from "@/app-utils/string.js";
import { selMessage } from "@/messages/message.js";
import type { BrowsingDirectoryFragment } from "@/modules/files/graphql/files-queries.gql.js";
import { isAnalysisSetFile, isBamFile, isBedFile } from "@/modules/files/utils/file.js";
import { type IFileUpload, uploadCollectorResources } from "@/redux/modules/files.js";
import type { IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.js";
import { FILE_BROWSE_TYPES, MAX_LENGTH } from "@/settings/settings.js";
import FileDropzone, { type IFileDropzoneProps } from "@/ui-modules/core/forms/FileDropzone.js";
import InputField, { InputReduxFormField } from "@/ui-modules/core/redux-form/InputField.js";

import { SequencedGenomeSelector } from "./SequencedGenomeSelector.js";

interface IFileUploadProps extends Partial<IFileDropzoneProps> {
  setUploadValidationError: (validationErrorMessage: string | null) => void;
  setDropZoneOpenCallback: (callback: any) => void;
  notAuthorized?: boolean;
  currentFolder: BrowsingDirectoryFragment;
}

export const FileUpload: FC<PropsWithChildren<IFileUploadProps>> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [uploadFilesList, setUploadFilesList] = useState<IFileUpload[]>([]);

  const forbiddenFileTypes = useSelector<IAppState, string[]>(
    (state) => state.config.configuration.application?.forbiddenUploads as string[],
  );

  const sendFiles = useCallback(
    (files: IFileUpload[]) => {
      setUploadFilesList([]); // reset the list
      void dispatch(uploadCollectorResources(props.currentFolder, files));
    },
    [dispatch, setUploadFilesList, props.currentFolder],
  );

  const verifyFiles = useCallback(
    (files: File[]) => {
      const uploadContainsForbiddenFileTypes = files.some((file) =>
        forbiddenFileTypes.includes(getFilenameInfo(file.name.toLowerCase())?.extension!),
      );

      if (uploadContainsForbiddenFileTypes) {
        props.setUploadValidationError(
          selMessage({
            id: "filesPage.uploadValidation.rejectedTypeMessage",
            values: { type: forbiddenFileTypes.join(", ") },
          }),
        );
      } else {
        props.setUploadValidationError(null);
        const uploadFiles: IFileUpload[] = files.map((file: File) => ({ file, organismId: undefined }));
        // in case some files are datasets, we need to have additional modal dialog for annotations
        if (getFilesToAnnotate(uploadFiles).length) {
          setUploadFilesList(uploadFiles);
        } else {
          sendFiles(uploadFiles);
        }
      }
    },
    [dispatch, props.currentFolder, sendFiles, setUploadFilesList],
  );

  return (
    <FileDropzone
      addResources={verifyFiles}
      showAlways={false}
      notAuthorized={props.notAuthorized}
      showFileDropWarningMessage={props.setUploadValidationError}
      setDropZoneOpenCallback={props.setDropZoneOpenCallback}
    >
      {props.children}
      {getFilesToAnnotate(uploadFilesList).length > 0 && (
        <DatasetAnnotationDialogForm
          onApply={sendFiles}
          onCancel={() => setUploadFilesList([])}
          files={uploadFilesList}
        />
      )}
    </FileDropzone>
  );
};

interface IFileUploadDialogProps {
  files: IFileUpload[];
  onApply: (files: IFileUpload[]) => void;
  onCancel: () => void;
}

const DatasetAnnotationDialog: FC<IFileUploadDialogProps & InjectedFormProps<any, any>> = (props) => {
  const { handleSubmit, submitting, error } = props;

  const { dialogProps, dialogRef, underlayProps } = useControlledDialog(props.onCancel);

  const GENOME_FIELD_NAME = "genome";
  const DESCRIPTION_FIELD_NAME = "description";

  const filesToAnnotate: IFileUpload[] = getFilesToAnnotate(props.files);
  const bamFileIncluded: boolean = isBamFileIncluded(filesToAnnotate);
  const bedFileIncluded: boolean = isBedFileIncluded(filesToAnnotate);
  const homogeneousFileExtension: string | null | undefined = getHomogeneousFileExtension(filesToAnnotate);

  function handleSubmitData(values: Value) {
    const description: string = get(values, DESCRIPTION_FIELD_NAME);
    const bamOrganismId: number = get(values, GENOME_FIELD_NAME)?.id;

    props.onApply(
      filesToAnnotate
        .map((file): IFileUpload => {
          const organismId: number = get(values, file.file.name)?.id; // use deep get because name contains "." ; selecting an organism is optional
          return { ...file, organismId: organismId || bamOrganismId, description: description };
        })
        .concat(
          props.files
            .filter((file) => !filesToAnnotate.includes(file))
            .map((file): IFileUpload => {
              return { ...file, organismId: null, description: description };
            }),
        ),
    );
  }

  return (
    <Dialog
      ref={dialogRef}
      domProps={dialogProps}
      underlayProps={underlayProps}
      title="Genome Association"
      mainButton={{
        intent: ActionButtonIntent.Primary,
        label: "Save and Upload",
        onPress: handleSubmit(handleSubmitData),
        isDisabled: submitting,
        isLoading: submitting,
      }}
      errorCallout={{ message: error }}
      closeFn={props.onCancel}
      width={580}
    >
      <VStack>
        <div style={{ marginBottom: 10 }}>
          {`Would you like to attach an organism to the ${
            homogeneousFileExtension ? `${homogeneousFileExtension} ` : ""
          }${pluralizeWord("file", filesToAnnotate.length)}?`}
        </div>
        {homogeneousFileExtension ? (
          <SequencedGenomeSelector fieldName={GENOME_FIELD_NAME} name={"Genome"} submitting={submitting} />
        ) : (
          filesToAnnotate.map((file: IFileUpload, i) => {
            return (
              <div key={`genome-selector-${i}`} className="form-group">
                <SequencedGenomeSelector fieldName={file.file.name} submitting={submitting} />
              </div>
            );
          })
        )}
        {(bamFileIncluded || bedFileIncluded) && filesToAnnotate.length === 1 && (
          <InputReduxFormField
            componentClass="input"
            name={DESCRIPTION_FIELD_NAME}
            label="Description"
            placeholder="Optional description"
            maxLength={MAX_LENGTH.RESOURCE.DESCRIPTION}
            component={InputField}
          />
        )}
      </VStack>
    </Dialog>
  );
};

enum DatasetAnnotationDialogFormNames {
  FORM_NAME = "datasetAnnotationUploadForm",
}

const DatasetAnnotationDialogForm = reduxForm<unknown, IFileUploadDialogProps>({
  form: DatasetAnnotationDialogFormNames.FORM_NAME,
})(
  DatasetAnnotationDialog as ComponentType<IFileUploadDialogProps & InjectedFormProps<unknown, IFileUploadDialogProps>>,
);

function getFilesToAnnotate(files: IFileUpload[]) {
  return files.filter(
    (uploadFile: IFileUpload) =>
      // we upload a file so we can consider it a regular file. For the check it is just important that
      // it is not of type Hyperlink
      (isAnalysisSetFile({ fileName: uploadFile.file.name, fileBrowseType: FILE_BROWSE_TYPES.REGULAR_FILE }) ||
        isBamFile({ fileName: uploadFile.file.name, fileBrowseType: FILE_BROWSE_TYPES.REGULAR_FILE }) ||
        isBedFile({ fileName: uploadFile.file.name, fileBrowseType: FILE_BROWSE_TYPES.REGULAR_FILE })) &&
      uploadFile.organismId === undefined,
  ); // null is a valid value!
}

function isBamFileIncluded(files: IFileUpload[]): boolean {
  return (
    files.filter((uploadFile: IFileUpload) =>
      // we upload a file so it will be a project file. For the check it is just important that
      // it is not of type Hyperlink
      isBamFile({ fileName: uploadFile.file.name, fileBrowseType: FILE_BROWSE_TYPES.REGULAR_FILE }),
    ).length > 0
  );
}

function isBedFileIncluded(files: IFileUpload[]): boolean {
  // we upload a file so we can consider it a regular file. For the check it is just important that
  // it is not of type Hyperlink
  return (
    files.filter((uploadFile: IFileUpload) =>
      isBedFile({ fileName: uploadFile.file.name, fileBrowseType: FILE_BROWSE_TYPES.REGULAR_FILE }),
    ).length > 0
  );
}

// Return the file extension of the files if they have all the same one. Otherwise, return null.
function getHomogeneousFileExtension(files: IFileUpload[]): string | null | undefined {
  const fileTypes = files.map((f: IFileUpload) => getFilenameInfo(f.file.name)?.extension);
  return uniq(fileTypes).length === 1 ? fileTypes[0] : null;
}
